import { useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'es-toolkit';

import { logError } from '@zg-rentals/log-error';
import { DatarouterError } from '@zg-rentals/web-base';
import { DEFAULT_ERROR_MESSAGE } from '@zg-rentals/zillow-rental-manager/constants';

import { getDataToSave } from '../../../utils/amenitiesUtils';
import { getFirstUnitError } from '../../../utils/multiplexUpdateErrorUtils';

import TimeConstants from '../../../constants/TimeConstants';

import { MULTIPLEX_AMENITY_KEYS } from './useMultiplexUpdate';

import type { UserPropertyMultiplexUpdateV2RequestDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { GetFirstUnitErrorOptions } from '../../../utils/multiplexUpdateErrorUtils';
import type { UseMultiplexUpdateResult } from './useMultiplexUpdate';

type MultiplexUpdateFields = Omit<UserPropertyMultiplexUpdateV2RequestDto, 'multiplexBuildingSessionId'>;

type UseDebouncedMultiplexSaveParams<TValue> = {
  multiplexBuildingSessionId: string | undefined;
  multiplexUpdateAsync: UseMultiplexUpdateResult['mutateAsync'];
  buildPostData: (value: TValue) => MultiplexUpdateFields | Partial<MultiplexUpdateFields>;
  getUnitErrorOptions: (value: TValue) => GetFirstUnitErrorOptions & { fallbackMessage: string };
  contextDetails: string;
  shouldSkipSave?: (value: TValue, postData: MultiplexUpdateFields | Partial<MultiplexUpdateFields>) => boolean;
};

/**
 * Debounced multiplex save with stable debounce callback (refs hold latest session/mutate).
 * Same pattern as useFieldSave — avoids orphaned timers when mutateAsync identity changes.
 */
export const useDebouncedMultiplexSave = <TValue>({
  multiplexBuildingSessionId,
  multiplexUpdateAsync,
  buildPostData,
  getUnitErrorOptions,
  contextDetails,
  shouldSkipSave,
}: UseDebouncedMultiplexSaveParams<TValue>) => {
  const [error, setError] = useState<string | null>(null);

  const latest = useRef({
    multiplexBuildingSessionId,
    multiplexUpdateAsync,
    buildPostData,
    getUnitErrorOptions,
    contextDetails,
    shouldSkipSave,
    setError,
  });
  latest.current = {
    multiplexBuildingSessionId,
    multiplexUpdateAsync,
    buildPostData,
    getUnitErrorOptions,
    contextDetails,
    shouldSkipSave,
    setError,
  };

  const saveDebounced = useMemo(
    () =>
      debounce((value: TValue) => {
        const {
          multiplexBuildingSessionId,
          multiplexUpdateAsync: updateAsync,
          buildPostData: build,
          getUnitErrorOptions: getErrorOptions,
          contextDetails: details,
          shouldSkipSave: skip,
          setError: setSaveError,
        } = latest.current;

        if (!multiplexBuildingSessionId) {
          logError({
            error: new Error('Unable to save: missing multiplexBuildingSessionId'),
            context: { details },
          });
          return;
        }

        const postData = build(value);
        if (skip?.(value, postData)) {
          return;
        }

        void (async () => {
          try {
            setSaveError(null);
            const response = await updateAsync({ postData: { multiplexBuildingSessionId, ...postData } });

            const unitErrorMessage = getFirstUnitError(response.units, {
              ...getErrorOptions(value),
              responseFailed: response.success === false,
            });

            if (unitErrorMessage) {
              setSaveError(unitErrorMessage);
            }
          } catch (err: unknown) {
            const message = DatarouterError.is(err)
              ? DatarouterError.getDisplayMessage(err)
              : ((err as { message?: string })?.message ?? DEFAULT_ERROR_MESSAGE);
            setSaveError(message);
          }
        })();
      }, TimeConstants.SELECTION_DEBOUNCE_TIME_MS),
    [],
  );

  useEffect(() => () => saveDebounced.cancel(), [saveDebounced]);

  return { saveDebounced, error };
};

const pickMultiplexAmenityPostData = (propertyRecord: Record<string, unknown>): Partial<MultiplexUpdateFields> => {
  const out: Partial<MultiplexUpdateFields> = {};
  for (const key of MULTIPLEX_AMENITY_KEYS) {
    const value = propertyRecord[key];
    if (typeof value === 'boolean') {
      out[key] = value;
    }
  }
  return out;
};

type AmenitySaveParams = {
  listingAlias: string;
  categorizedAmenitiesKeys: Record<string, Array<string>>;
  multiplexBuildingSessionId: string | undefined;
  multiplexUpdateAsync: UseMultiplexUpdateResult['mutateAsync'];
};

/**
 * Debounced multiplex amenity save for the property amenities step.
 */
export const useDebouncedMultiplexAmenitySave = ({
  listingAlias,
  categorizedAmenitiesKeys,
  multiplexBuildingSessionId,
  multiplexUpdateAsync,
}: AmenitySaveParams) =>
  useDebouncedMultiplexSave({
    multiplexBuildingSessionId,
    multiplexUpdateAsync,
    buildPostData: (inputsToSave: Record<string, boolean | string>) => {
      const dataToSave = getDataToSave({
        inputs: inputsToSave,
        categorizedAmenitiesKeys,
        listingAlias,
      });
      const propertyRecord = (dataToSave.property ?? {}) as unknown as Record<string, unknown>;
      return pickMultiplexAmenityPostData(propertyRecord);
    },
    shouldSkipSave: (_inputsToSave, postData) => Object.keys(postData).length === 0,
    getUnitErrorOptions: () => ({
      fieldNames: MULTIPLEX_AMENITY_KEYS,
      fallbackMessage: 'Unable to save amenities',
    }),
    contextDetails: 'useDebouncedMultiplexAmenitySave',
  });
